import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useSocket } from '../../context/SocketContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useInviteGate } from '../../context/InviteGateContext';
import NotificationCenter from '../NotificationCenter';
import API from '../../utils/api';

export default function Nav() {
  const { user, logout } = useAuth();
  // UX review: while the invite gate locks a logged-out visitor out, every marketplace link, the search,
  // the cart and "+ List item" only lead to InviteSplash, so they are hidden and the sign-up CTA becomes
  // "Enter invite code".
  const { locked: gateLocked, enterInvite } = useInviteGate();
  const { cartItem } = useCart();
  const { onNewMessage } = useSocket() || {};
  useCurrency(); // keeps context wired; currency display is USD-only
  const navigate  = useNavigate();
  const location  = useLocation();
  const [scrolled,       setScrolled]       = useState(false);
  const [q,              setQ]              = useState('');
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [isDark,         setIsDark]         = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [suggestions,    setSuggestions]    = useState([]);
  const [showSuggest,    setShowSuggest]    = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cpkd_recent_searches') || '[]'); } catch { return []; }
  });
  const msgPollRef    = useRef(null);
  const suggestTimer  = useRef(null);
  const userRef = useRef(user);

  const saveRecentSearch = (term) => {
    if (!term.trim()) return;
    const updated = [term.trim(), ...recentSearches.filter(s => s !== term.trim())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('cpkd_recent_searches', JSON.stringify(updated));
  };
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Poll unread message count every 60s when logged in
  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    const fetch = () => API.get('/messages/unread-count').then(r => setUnreadMessages(r.data.count || 0)).catch(() => {});
    fetch();
    msgPollRef.current = setInterval(fetch, 60000);
    return () => clearInterval(msgPollRef.current);
  }, [user]);

  // Real-time: bump unread count immediately when a new message arrives from someone else
  useEffect(() => {
    if (!onNewMessage) return;
    return onNewMessage((msg) => {
      if (msg.sender_id === userRef.current?.id) return;
      if (window.location.pathname.startsWith('/messages')) return;
      setUnreadMessages(prev => prev + 1);
    });
  }, [onNewMessage]); // eslint-disable-line

  // Reset unread count when navigating to messages
  useEffect(() => {
    if (location.pathname === '/messages') setUnreadMessages(0);
  }, [location.pathname]);

  // Close mobile menu on route change (including query param changes for type filters)
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  // Keep nav search box in sync with /browse?q=… (clear when q is absent)
  useEffect(() => {
    if (location.pathname !== '/browse') return;
    const urlQ = new URLSearchParams(location.search).get('q') || '';
    setQ(urlQ);
  }, [location.pathname, location.search]);

  // Track theme changes from the DarkModeToggle
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    // QA r3: lets global.css hide the floating camera / theme buttons over the open menu.
    document.body.classList.toggle('nav-menu-open', mobileOpen);
    return () => { document.body.style.overflow = ''; document.body.classList.remove('nav-menu-open'); };
  }, [mobileOpen]);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (q.trim()) { saveRecentSearch(q); navigate(`/browse?q=${encodeURIComponent(q)}`); setMobileOpen(false); }
  };

  const isActive = (path) => {
    if (path.includes('?')) {
      const [p, s] = path.split('?');
      return location.pathname === p && location.search === `?${s}`;
    }
    return location.pathname === path;
  };

  const handleLogout = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setMenuOpen(false);
    setMobileOpen(false);
    await logout();
    navigate('/');
  };

  const navLinks = [
    { label: 'Browse',         path: '/browse' },
    { label: 'Rentals',        path: '/browse?type=rent' },
    { label: 'Auctions',       path: '/browse?type=auction' },
    { label: 'For Sale',       path: '/browse?type=sale' },
    { label: 'Barter',         path: '/barter' },
    { label: 'Free',           path: '/free' },
    { label: '🔍 Looking For', path: '/looking-for' },
    { label: '🔴 Live',        path: '/live' },
    { label: 'How it Works',   path: '/how-it-works' },
  ];

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        {/* Logo */}
        <a href="/" className="nav-logo" style={{ textDecoration:'none', color:'inherit' }}>
          <span className="nav-logo-emoji">🍒</span>
          <span className="nav-logo-text">
            <span className="red">cherry</span>pkd
          </span>
        </a>

        {/* Search — desktop only */}
        {!gateLocked && (
        <div className="nav-search hide-mobile" style={{ flex: 1, maxWidth: 380, position: 'relative' }}>
          <span className="nav-search-icon">🔍</span>
          <input
            className="nav-search-input"
            placeholder="Search for anything near you..."
            value={q}
            autoComplete="off"
            onChange={e => {
              const val = e.target.value;
              setQ(val);
              clearTimeout(suggestTimer.current);
              if (val.length < 2) { setSuggestions([]); setShowSuggest(false); return; }
              suggestTimer.current = setTimeout(async () => {
                try {
                  const { data } = await API.get(`/listings?q=${encodeURIComponent(val)}&limit=6`);
                  const titles = (data.listings || []).map(l => l.title).filter((t,i,a) => a.indexOf(t) === i).slice(0,6);
                  setSuggestions(titles);
                  setShowSuggest(titles.length > 0);
                } catch {}
              }, 220);
            }}
            onKeyDown={e => { if (e.key === 'Enter') { setShowSuggest(false); handleSearch(); } }}
            onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
            onFocus={() => {
              if (q.length < 2 && recentSearches.length > 0) setShowSuggest(true);
              else if (suggestions.length > 0) setShowSuggest(true);
            }}
          />
          {showSuggest && (suggestions.length > 0 || (q.length < 2 && recentSearches.length > 0)) && (
            <div style={{ position:'absolute', top:'100%', left:0, right:0, background: isDark ? '#252220' : 'white', border:'1px solid var(--border)', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,.12)', zIndex:400, overflow:'hidden', marginTop:4 }}>
              {q.length < 2 && recentSearches.length > 0 ? (
                <>
                  <div style={{ padding:'8px 14px 4px', fontSize:11, color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:.5, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>Recent searches</span>
                    <span onMouseDown={e => { e.preventDefault(); setRecentSearches([]); localStorage.removeItem('cpkd_recent_searches'); setShowSuggest(false); }} style={{ cursor:'pointer', color:'var(--red)', fontWeight:500, fontSize:11 }}>Clear</span>
                  </div>
                  {recentSearches.map((s,i) => (
                    <div key={i} onMouseDown={() => { setQ(s); saveRecentSearch(s); setShowSuggest(false); navigate(`/browse?q=${encodeURIComponent(s)}`); }}
                      style={{ padding:'10px 14px', fontSize:13, cursor:'pointer', borderBottom: i < recentSearches.length-1 ? '1px solid var(--border)' : 'none', display:'flex', alignItems:'center', gap:8, background:'var(--white)' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                      onMouseLeave={e => e.currentTarget.style.background= isDark ? '#252220' : 'white'}>
                      🕐 {s}
                    </div>
                  ))}
                </>
              ) : (
                suggestions.map((s,i) => (
                  <div key={i} onMouseDown={() => { setQ(s); saveRecentSearch(s); setShowSuggest(false); navigate(`/browse?q=${encodeURIComponent(s)}`); }}
                    style={{ padding:'10px 14px', fontSize:13, cursor:'pointer', borderBottom: i < suggestions.length-1 ? '1px solid var(--border)' : 'none', display:'flex', alignItems:'center', gap:8, background:'var(--white)' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                    onMouseLeave={e => e.currentTarget.style.background= isDark ? '#252220' : 'white'}>
                    🔍 {s}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        )}

        {/* Links — desktop only */}
        {!gateLocked && (
        <div className="nav-links hide-mobile">
          {navLinks.map(l => (
            <button
              key={l.path}
              className={`nav-link ${isActive(l.path) ? 'active' : ''}`}
              onClick={() => navigate(l.path)}
              style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', padding: '8px 12px', borderRadius: 6, transition: 'all .15s', background: 'transparent', border: 'none' }}
              onMouseEnter={e => { e.target.style.background = 'var(--cream)'; e.target.style.color = 'var(--cherry)'; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = 'var(--text-primary)'; }}
            >{l.label}</button>
          ))}
        </div>
        )}

        {/* Actions */}
        <div className="nav-actions" style={gateLocked ? { marginLeft: 'auto' } : undefined}>
          {/* Cart */}
          {!gateLocked && (
          <button className="nav-icon-btn" onClick={() => navigate('/checkout')} title="Cart" aria-label="Cart" style={{ position: 'relative' }}>
            🛒
            {cartItem && (
              <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--cherry)', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
            )}
          </button>
          )}

          {user ? (
            <>
              <NotificationCenter />

              <button className="nav-icon-btn hide-mobile" onClick={() => navigate('/messages')} title="Messages" aria-label="Messages" style={{ position:'relative' }}>
                💬
                {unreadMessages > 0 && (
                  <span style={{ position:'absolute', top:-4, right:-4, minWidth:16, height:16, borderRadius:8, background:'var(--cherry)', color:'white', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', lineHeight:1 }}>
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </button>

              {/* Avatar / dropdown — desktop */}
              <div style={{ position: 'relative' }} className="hide-mobile">
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  aria-label="Account menu"
                  aria-expanded={menuOpen}
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid var(--cherry)', background: user.avatarUrl ? 'transparent' : 'var(--cherry-light)', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--cherry)' }}
                >
                  {user.avatarUrl
                    ? <img src={user.avatarUrl} alt={user.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : user.firstName?.[0]?.toUpperCase()}
                </button>

                {menuOpen && (
                  <div
                    style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: isDark ? '#252220' : 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 8, minWidth: 200, boxShadow: 'var(--shadow-md)', zIndex: 200, animation: 'slideDown .2s ease' }}
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{user.firstName} {user.lastName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{user.email}</div>
                    </div>
                    {[
                      { label: '📊 Dashboard',          path: '/dashboard' },
                      { label: '📋 My Listings',        path: '/dashboard/listings' },
                      { label: '📦 My Orders',          path: '/dashboard/orders' },
                      { label: '📅 My Bookings',        path: '/dashboard/bookings' },
                      { label: '❤️ Saved',              path: '/dashboard/saved' },
                      { label: '🔔 Watchlist & Alerts', path: '/dashboard/watchlist' },
                      { label: '🔍 Looking For Posts',  path: '/looking-for' },
                      { label: '💬 Messages',           path: '/messages' },
                      { label: '⚙️ Account Settings',   path: '/settings' },
                    ].map(item => (
                      <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderRadius: 8, transition: 'background .15s' }}
                        onMouseEnter={e => e.target.style.background = 'var(--cream)'}
                        onMouseLeave={e => e.target.style.background = 'none'}
                      >{item.label}</button>
                    ))}
                    {['admin','superadmin','moderator'].includes(user.role) && (
                      <button onClick={() => { navigate('/admin'); setMenuOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--cherry)', fontWeight: 700, borderRadius: 8 }}>
                        🛡️ Admin Panel
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4 }}>
                      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleLogout}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-soft)', borderRadius: 8 }}>
                        🚪 Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm hide-mobile" onClick={() => navigate('/login')}>Log in</button>
              {gateLocked
                ? <button className="btn btn-cherry btn-sm hide-mobile" onClick={enterInvite}>Enter invite code</button>
                : <button className="btn btn-cherry btn-sm hide-mobile" onClick={() => navigate('/signup')}>Get started</button>}
            </>
          )}

          {!gateLocked && (
          <button className="btn btn-cherry btn-sm hide-mobile" style={{ marginLeft: 4 }} onClick={() => navigate('/list')}>
            + List item
          </button>
          )}

          {/* Search icon — mobile only, opens the menu which has search at the top */}
          {!gateLocked && (
          <button
            className="show-mobile nav-search-btn"
            onClick={() => setMobileOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '11px 8px', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}
            aria-label="Search"
          >🔍</button>
          )}

          {/* Hamburger — mobile only */}
          <button
            className="show-mobile nav-burger"
            onClick={() => setMobileOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '11px 11px', display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}
            aria-label="Menu"
          >
            <span style={{ display: 'block', width: 22, height: 2, background: mobileOpen ? 'transparent' : 'var(--charcoal)', borderRadius: 2, transition: 'all .2s' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--charcoal)', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(45deg) translate(5px, -5px)' : 'none' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--charcoal)', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(-45deg) translate(5px, 5px)' : 'none' }} />
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu Overlay ────────────────────────────── */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', flexDirection: 'column' }}
          onClick={e => { if (e.target === e.currentTarget) setMobileOpen(false); }}
        >
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)' }} onClick={() => setMobileOpen(false)} />

          {/* Drawer */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: isDark ? '#1E1B18' : 'white', maxHeight: '92vh', overflowY: 'auto', borderBottomLeftRadius: 20, borderBottomRightRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,.2)', paddingBottom: 24 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 20 }}>
                <span style={{ color: 'var(--cherry)' }}>cherry</span>pkd
              </div>
              <button className="tap-target" onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-soft)', padding: 4 }}>✕</button>
            </div>

            {/* Mobile Search */}
            {!gateLocked && (
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
                <input
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: 'none', background: isDark ? '#252220' : 'white', color: 'var(--text-primary)' }}
                  placeholder="Search..."
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
                <button type="submit" className="btn btn-cherry btn-sm">Go</button>
              </form>
              {q.length < 2 && recentSearches.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Recent</span>
                    <span onClick={() => { setRecentSearches([]); localStorage.removeItem('cpkd_recent_searches'); }} style={{ cursor: 'pointer', color: 'var(--red)', fontWeight: 500 }}>Clear</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {recentSearches.map((s, i) => (
                      <button key={i} type="button" onClick={() => { saveRecentSearch(s); setMobileOpen(false); navigate(`/browse?q=${encodeURIComponent(s)}`); }}
                        style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 100, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-primary)', fontFamily: "'DM Sans',sans-serif" }}>
                        🕐 {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* User info (if logged in) */}
            {user && (
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid var(--cherry)', background: 'var(--cherry-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'var(--cherry)', overflow: 'hidden', flexShrink: 0 }}>
                  {user.avatarUrl ? <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : user.firstName?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{user.firstName} {user.lastName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{user.email}</div>
                </div>
              </div>
            )}

            {/* Nav links */}
            {gateLocked ? (
            <p style={{ padding: '14px 20px 4px', margin: 0, fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.5 }}>
              CherryPKD is invite-only during the soft launch. Log in, or enter your invite code to browse.
            </p>
            ) : (
            <div style={{ padding: '8px 12px' }}>
              {navLinks.map(l => (
                <button key={l.path} onClick={() => navigate(l.path)}
                  style={{ display: 'block', width: '100%', padding: '12px 12px', textAlign: 'left', background: isActive(l.path) ? 'var(--cherry-light)' : 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: isActive(l.path) ? 'var(--cherry)' : 'var(--text-primary)', borderRadius: 10, fontFamily: "'DM Sans',sans-serif", fontWeight: isActive(l.path) ? 700 : 500, marginBottom: 2 }}>
                  {l.label}
                </button>
              ))}
            </div>
            )}

            {/* Account links (logged in) */}
            {user && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '8px 12px', marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-soft)', padding: '6px 12px 10px' }}>Account</div>
                {[
                  { label: '📊 Dashboard',          path: '/dashboard' },
                  { label: '📋 My Listings',        path: '/dashboard/listings' },
                  { label: '📦 My Orders',          path: '/dashboard/orders' },
                  { label: '💬 Messages',           path: '/messages' },
                  { label: '🔔 Watchlist & Alerts', path: '/dashboard/watchlist' },
                  { label: '⚙️ Settings',           path: '/settings' },
                ].map(item => (
                  <button key={item.path} onClick={() => navigate(item.path)}
                    style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)', borderRadius: 10, marginBottom: 2 }}>
                    {item.label}
                  </button>
                ))}
                {['admin','superadmin','moderator'].includes(user.role) && (
                  <button onClick={() => navigate('/admin')}
                    style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--cherry)', fontWeight: 700, borderRadius: 10 }}>
                    🛡️ Admin Panel
                  </button>
                )}
                <button type="button" onClick={handleLogout}
                  style={{ display: 'block', width: '100%', padding: '11px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-soft)', borderRadius: 10 }}>
                  🚪 Log out
                </button>
              </div>
            )}

            {/* CTA buttons */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
              {gateLocked
                ? <button className="btn btn-cherry btn-full" onClick={() => { setMobileOpen(false); enterInvite(); }} style={{ flex: 1 }}>Enter invite code</button>
                : <button className="btn btn-cherry btn-full" onClick={() => navigate('/list')} style={{ flex: 1 }}>+ List item</button>}
              {!user && <button className="btn btn-ghost btn-full" onClick={() => navigate('/login')} style={{ flex: 1 }}>Log in</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
